import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import QuoteForm from './components/QuoteForm';
import { WHATSAPP_URL } from '@/constants/whatsapp';
import { getOrganizationSchema, getWebPageSchema, SCHEMA_SITE_URL } from '@/constants/schema';

const SITE_URL = import.meta.env.VITE_SITE_URL as string;

const PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    getWebPageSchema({
      path: '/request-quote',
      name: 'Request Wholesale Gloves Quote Oman | 24hr Response | omandisposables.com',
      description: 'Request a wholesale disposable gloves quote from omandisposables.com, Oman. Our team responds within 24 hours via WhatsApp or email for bulk medical, food, lab & industrial gloves.',
      breadcrumbId: `${SCHEMA_SITE_URL}/request-quote#breadcrumb`,
    }),
    {
      '@type': 'BreadcrumbList',
      '@id': `${SCHEMA_SITE_URL}/request-quote#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Request a Quote', item: `${SITE_URL}/request-quote` },
      ],
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${SCHEMA_SITE_URL}/request-quote#localbusiness`,
      'name': 'Oman Disposables',
      'image': 'https://omandisposables.com/logo.png',
      'url': 'https://omandisposables.com',
      'telephone': '+96896052773',
      'description': 'Wholesale disposable gloves supplier Oman — nitrile, latex, vinyl gloves for medical, food and industrial use. Est. 1996.',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Muscat',
        'addressRegion': 'Muscat',
        'addressCountry': 'OM',
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': '23.5880',
        'longitude': '58.3829',
      },
      'openingHoursSpecification': {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': [
          'Saturday',
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
        ],
        'opens': '08:00',
        'closes': '18:00',
      },
      'priceRange': '$$',
    },
    {
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/request-quote/#page`,
      name: 'Request a Wholesale Gloves Quote — omandisposables.com',
      url: `${SITE_URL}/request-quote`,
      description: 'Request a wholesale disposable gloves quote from omandisposables.com. Our team in Oman responds within 24 hours via WhatsApp or email.',
      mainEntity: {
        '@type': 'Organization',
        name: 'omandisposables.com',
        url: SITE_URL,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'sales',
          availableLanguage: ['English', 'Arabic'],
          areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'],
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Sunday'],
            opens: '08:00',
            closes: '18:00',
          },
        },
      },
    },
  ],
};

export default function RequestQuotePage() {
  return (
    <main>
      <title>Request Wholesale Gloves Quote Oman | 24hr Response | omandisposables.com</title>
      <meta name="description" content="Request a wholesale disposable gloves quote from omandisposables.com, Oman. Our team responds within 24 hours via WhatsApp or email for bulk medical, food, lab & industrial gloves." />
      <meta name="keywords" content="wholesale gloves quote Oman, request gloves pricing Muscat, bulk gloves inquiry GCC, disposable gloves quote" />
      <link rel="canonical" href={`${SITE_URL}/request-quote`} />
      <meta property="og:title" content="Request Wholesale Gloves Quote | omandisposables.com Oman" />
      <meta property="og:description" content="Get a wholesale gloves quote from omandisposables.com. 24-hour response via WhatsApp or email for bulk orders across Oman & GCC." />
      <meta property="og:url" content={`${SITE_URL}/request-quote`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://public.readdy.ai/ai/img_res/06cd29516bf9035491fbb242a18239cc.jpg" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Request Wholesale Gloves Quote | omandisposables.com" />
      <meta name="twitter:description" content="24hr response for wholesale gloves quote. WhatsApp or email — bulk medical, food, lab & industrial gloves from Oman." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=professional%20business%20person%20using%20smartphone%20and%20laptop%20for%20wholesale%20procurement%20in%20modern%20office%2C%20clean%20minimal%20workspace%20with%20document%20and%20contract%20on%20desk%2C%20warm%20professional%20lighting%20in%20corporate%20office%20setting&width=1200&height=630&seq=quote-hero-og&orientation=landscape" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PAGE_SCHEMA) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-12 bg-navy overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20business%20person%20using%20smartphone%20and%20laptop%20for%20wholesale%20procurement%20in%20modern%20office%2C%20clean%20minimal%20workspace%20with%20document%20and%20contract%20on%20desk%2C%20warm%20professional%20lighting%20in%20corporate%20office%20setting&width=1920&height=500&seq=quote-hero-01&orientation=landscape"
            alt="Request a Wholesale Quote - omandisposables.com Oman"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/90 to-navy/95" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-5">
            🇴🇲 Trusted Wholesale Supply — Based in Oman
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl mb-2">
            Request a Wholesale Quote
          </h1>
          <p className="font-arabic text-white/80 text-2xl md:text-3xl font-bold mb-3" dir="rtl">
            طلب عرض سعر بالجملة
          </p>
          <p className="text-white/65 text-sm md:text-base max-w-xl mx-auto">
            Choose your preferred contact method below. Our wholesale team responds within 24 hours.
          </p>
          <p className="font-arabic text-white/50 text-sm mt-1 max-w-xl mx-auto" dir="rtl">
            اختر طريقة التواصل المفضلة لديك. يستجيب فريق الجملة لدينا خلال 24 ساعة.
          </p>
        </div>
      </section>

      {/* Main Two-Column Layout */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* Option A — WhatsApp (Recommended) */}
            <div className="bg-white rounded-2xl border-2 border-brand-green overflow-hidden">
              <div className="bg-brand-green px-6 py-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-white text-2xl" />
                </div>
                <div>
                  <h2 className="font-heading font-black text-white text-lg leading-tight">Option A — WhatsApp</h2>
                  <span className="text-white/80 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">⭐ Recommended — Fastest Response</span>
                </div>
              </div>

              <div className="p-6">
                <p className="font-arabic text-gray-600 text-base font-bold mb-1 text-right" dir="rtl">الخيار الأول — واتساب</p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Message our wholesale team directly on WhatsApp. Get pricing, product specs and availability in real time.
                  Pre-filled message included — just tap and send.
                </p>
                <p className="font-arabic text-gray-400 text-xs leading-relaxed mb-6" dir="rtl">
                  راسل فريق الجملة لدينا مباشرة على واتساب. احصل على الأسعار ومواصفات المنتجات والتوفر في الوقت الفعلي.
                </p>

                {/* Response time badges */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { icon: 'ri-time-line', label: 'Response Time', value: 'Under 24 Hours' },
                    { icon: 'ri-global-line', label: 'Languages', value: 'English & Arabic' },
                    { icon: 'ri-calendar-line', label: 'Availability', value: 'Sat–Thu, 8am–6pm' },
                    { icon: 'ri-price-tag-3-line', label: 'Quote Type', value: 'Instant Pricing' },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <i className={`${item.icon} text-navy text-sm`} />
                        <span className="text-gray-400 text-xs">{item.label}</span>
                      </div>
                      <p className="font-heading font-bold text-navy text-sm">{item.value}</p>
                    </div>
                  ))}
                </div>

                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp text-base px-6 py-4 rounded-full w-full justify-center shadow-xl hover:scale-105 transition-transform"
                >
                  <i className="ri-whatsapp-line text-xl" />
                  💬 Open WhatsApp Now
                </a>

                <p className="text-gray-400 text-xs text-center mt-3">
                  Pre-filled: &ldquo;Hi omandisposables.com, I need wholesale pricing — omandisposables.com&rdquo;
                </p>
              </div>
            </div>

            {/* Option B — Quote Form */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-navy px-6 py-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-white text-2xl" />
                </div>
                <div>
                  <h2 className="font-heading font-black text-white text-lg leading-tight">Option B — Quote Form</h2>
                  <span className="text-white/70 text-xs">Response within 24 business hours</span>
                </div>
              </div>

              <div className="p-6">
                <p className="font-arabic text-gray-600 text-base font-bold mb-1 text-right" dir="rtl">الخيار الثاني — نموذج عرض السعر</p>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Prefer email? Complete the form below and our wholesale team will respond with a detailed quote within 24 hours.
                </p>
                <QuoteForm />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-8 bg-white border-t border-gray-100">
        <div className="container-max">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              
              { icon: 'ri-map-pin-line', text: 'Based in Oman — GCC Delivery' },
              { icon: 'ri-time-line', text: '24hr Response — WhatsApp & Email' },
              { icon: 'ri-group-line', text: '500+ Wholesale Clients' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-gray-500 text-sm">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-brand-green text-lg`} />
                </div>
                <span className="font-heading font-semibold">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />

      {/* Legal small text — plain, bottom of page only */}
      <div className="bg-white border-t border-gray-50 py-4 text-center">
        <p className="text-gray-300 text-[10px] tracking-wide">
          attiyaf al jazeera household utensils llc &nbsp;|&nbsp; omandisposables.com
        </p>
      </div>
    </main>
  );
}
